import {computed, ref, Ref, watch} from "vue";
import {Dayjs} from "dayjs";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import useLatestFetch from "@/core/composables/useLatestFetch";
import {mutate} from "swrv";
import {teamApi, teamPlanningApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import useTeamSchedule from "@/team/composables/useTeamSchedule";
import LegacyAccess from "@/core/interfaces/Access";
import {StaffingTarget, TeamAbsence, TeamShiftException} from "@/core/interfaces/TeamPlanning";
import {MemberDay, resolveMemberDay} from "@/planning/lib/weekPlanning";

/**
 * Bumped whenever something outside these screens writes a dated override.
 *
 * Every instance of this composable holds its own refs, so there is no cache
 * to invalidate: a counter they all watch is what lets a write made elsewhere
 * — the assistant applying a proposition — reach a range already on screen.
 * The agenda's presence rail is drawn from here, and the assistant's panel is
 * deliberately non-modal so that rail stays visible while it works: leaving it
 * on the previous hours after an application is the one outcome that would
 * make the pharmacist doubt the change happened at all.
 */
const revision = ref<number>(0);

/** Tell every mounted range to read its period again. */
export const invalidatePlanningRange = (): void => {
    revision.value++;
};

/**
 * Everything the planning needs over a date range: the team, its recurring
 * schedules, the dated overrides and approved absences of the range, and a
 * per-member day resolver.
 */
const usePlanningRange = (rangeStart: Ref<Dayjs>, rangeEnd: Ref<Dayjs>, enabled?: Ref<boolean>) => {
    const organizationId = useOrganizationId();
    const {schedulesByAccessId, upsertSchedule} = useTeamSchedule();

    const {data: teamData, isLoading: teamLoading} = useSwr(
        'access/current/team',
        () => teamApi().index()
    );

    const members = computed<LegacyAccess[]>(() => teamData.value?.team ?? []);

    const targetsKey = computed(() =>
        organizationId.value ? cacheKey(`organizations/${organizationId.value}/staffing-targets`) : null
    );

    const {data: targets} = useSwr(
        () => targetsKey.value,
        () => organizationId.value ? teamPlanningApi().staffingTargets(organizationId.value) : Promise.resolve([])
    );

    /** Write the grid a save just returned straight into the cache. */
    const setTargets = (next: StaffingTarget[]) => {
        if (targetsKey.value) {
            mutate(targetsKey.value, next);
        }
    };

    const exceptions = ref<TeamShiftException[]>([]);
    const absences = ref<TeamAbsence[]>([]);

    /**
     * Whether a range ever landed. A failure before the first success leaves
     * the grid without any absence at all, which consumers must word
     * differently from a stale-but-real period.
     */
    const rangeLoaded = ref<boolean>(false);

    /**
     * A failure keeps the previous range rather than blanking the screen, so
     * `failed` is what tells consumers not to present it as current.
     */
    const {loading: rangeLoading, failed: rangeFailed, run: runRange} = useLatestFetch();

    const fetchRange = async () => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId || enabled?.value === false) {
            return;
        }

        const filters = {
            from: rangeStart.value.format('YYYY-MM-DD'),
            to: rangeEnd.value.format('YYYY-MM-DD'),
        };

        await runRange(async isLatest => {
            const [nextExceptions, nextAbsences] = await Promise.all([
                teamPlanningApi().shiftExceptions(currentOrganizationId, filters),
                teamPlanningApi().absences(currentOrganizationId, filters),
            ]);

            if (isLatest()) {
                exceptions.value = nextExceptions;
                absences.value = nextAbsences;
                rangeLoaded.value = true;
            }
        });
    };

    watch(
        () => [
            organizationId.value,
            rangeStart.value.valueOf(),
            rangeEnd.value.valueOf(),
            enabled?.value ?? true,
            revision.value
        ],
        () => fetchRange(),
        {immediate: true}
    );

    const resolveDay = (member: LegacyAccess, date: Dayjs): MemberDay =>
        resolveMemberDay(
            member,
            schedulesByAccessId.value.get(member.id),
            exceptions.value,
            absences.value,
            date
        );

    return {
        organizationId,
        members,
        schedulesByAccessId,
        upsertSchedule,
        targets: computed(() => targets.value ?? []),
        setTargets,
        exceptions,
        absences,
        resolveDay,
        refreshRange: fetchRange,
        loading: computed(() => teamLoading.value || rangeLoading.value),
        failed: computed(() => rangeFailed.value),
        loaded: computed(() => rangeLoaded.value),
        /** Whether the team list ever landed: an empty list before that says nothing. */
        teamLoaded: computed(() => teamData.value !== undefined),
    };
};

export default usePlanningRange;

/** Monday of the week holding the given date. */
export const mondayOf = (date: Dayjs): Dayjs => date.startOf('day').subtract((date.day() + 6) % 7, 'day');

export const WEEK_DAY_COUNT = 7;

export const weekDates = (monday: Dayjs): Dayjs[] =>
    Array.from({length: WEEK_DAY_COUNT}, (_, index) => monday.add(index, 'day'));
